import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import useLocale from '@/hooks/useLocale';
import type {
  DetectedThreadsPayload,
  GatorProtectPortalModalsRootState,
  LocaleWithRoutes,
} from '../GatorProtectGlobals.types';
import type { GPModalsVariants } from '../gatorProtectModalsVisibilityReducer';
import {
  GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO,
  type UseGatorProtectAutomatedSecurityVideoParams,
  type UseGatorProtectAutomatedSecurityVideoReturn,
} from './GatorProtectAutomatedSecurityVideo.types';
import { FALLBACK_PORTAL_MODALS } from '../gatorProtectPortalModals.shared';
import { useSelector } from 'react-redux';
import { pickGatorProtectTrialVideoData } from '@/redux/modules/portalModals/portalModals.utils';
import { isBRServer } from '@/utils/Validators/validation';
import { MONETIZACAO_GATOR_PROTECT_VIDEO_MODAL } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

let videoPlayed = false;
export function useGatorProtectAutomatedSecurityVideo({
  summary,
  isHome,
  hideModalSlot,
}: UseGatorProtectAutomatedSecurityVideoParams): UseGatorProtectAutomatedSecurityVideoReturn {
  const history = useHistory();
  const { routes: routesLocale } = useLocale() as LocaleWithRoutes;
  const gatorProtectEnabled = summary.features?.gator_protect ?? false;

  const portalModalsState = useSelector((state: GatorProtectPortalModalsRootState) => state.portalModals);
  const apiModals = portalModalsState ?? FALLBACK_PORTAL_MODALS;
  const videoModalFeatureOn = useFeatureIsOn(MONETIZACAO_GATOR_PROTECT_VIDEO_MODAL);

  const automatedSecurityVideoData = useMemo((): DetectedThreadsPayload | null => {
    const data = pickGatorProtectTrialVideoData({
      modals: apiModals.modals,
    });
    const hostingId = data?.hosting_id;
    if (data == null || hostingId == null || Number.isNaN(Number(hostingId))) {
      return null;
    }
    return {
      threatCount: Math.max(0, Number(data.threats) || 0),
      hostingId: Number(hostingId),
    };
  }, [apiModals.modals]);

  const visibilityRule = Boolean(isHome && gatorProtectEnabled && automatedSecurityVideoData !== null)
    && !videoPlayed
    && isBRServer
    && videoModalFeatureOn;

  const hideAutomatedSecurityVideo = useCallback((topStackModalVariant: GPModalsVariants | null) => {
    videoPlayed = true;
    if (topStackModalVariant !== GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO) {
      return;
    }

    hideModalSlot(GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO);
  }, [hideModalSlot]);

  const redirectAutomatedSecurityVideo = useCallback(() => {
    hideModalSlot(GP_MODAL_VARIANT_AUTOMATED_SECURITY_VIDEO);
    history.push(routesLocale?.gatorProtect ?? '');
  }, [hideModalSlot, history, routesLocale?.gatorProtect]);

  return {
    data: {},
    utils: {
      hideAutomatedSecurityVideo,
      redirectAutomatedSecurityVideo,
    },
    visibilityRule,
  };
}
